import {
  ADD_CART_MUTATION_KEY,
  useCart,
  useDeleteCart,
  useEditCart,
  useOrders,
  type CartItem,
  type CartPlatformGroup,
  type ChannelAlias,
} from "@kupikupi/api";
import { API_BASE_URL } from "@kupikupi/brand";
import { convertKopecksToFen, formatDual, formatPrice, useExchangeRate } from "@kupikupi/money";
import { Link, useNavigate } from "@tanstack/react-router";
import { useIsMutating } from "@tanstack/react-query";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { TgEmpty } from "../components/TgEmpty";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { TgSkeleton } from "../components/TgSkeleton";
import { useAuth } from "../lib/auth";

// ─── Platform label map (mirrors web cart-page-client) ───────────────────────
const PLATFORM_LABEL: Record<ChannelAlias, { icon: string; label: string }> = {
  taobao: { icon: "🛍", label: "Taobao" },
  tmall: { icon: "🛒", label: "Tmall" },
  "1688": { icon: "🏭", label: "1688" },
  weidian: { icon: "🏪", label: "Weidian" },
  pdd: { icon: "🛒", label: "Pinduoduo" },
  douyin: { icon: "🎵", label: "Douyin" },
  jd: { icon: "📦", label: "JD" },
  xianyu: { icon: "🐟", label: "Xianyu" },
  merch: { icon: "🎁", label: "KupiKupi" },
};

// ─── resolveCartView (mirrors web, #431) ─────────────────────────────────────
type CartView = "skeleton" | "empty" | "cart";
function resolveCartView(input: {
  cartLoading: boolean;
  hasData: boolean;
  cartFetching: boolean;
  addingToCart: boolean;
  totalItems: number;
}): CartView {
  if (input.cartLoading || !input.hasData) return "skeleton";
  if (input.totalItems === 0) {
    if (input.addingToCart || input.cartFetching) return "skeleton";
    return "empty";
  }
  return "cart";
}

// ─── domestic freight helpers (mirrors web) ───────────────────────────────────
function lineDomesticExpressFee(
  item: Pick<CartItem, "channel" | "quantity" | "expressFeeRubKopecks" | "expressFeeCnyFen">,
  key: "expressFeeRubKopecks" | "expressFeeCnyFen",
): number {
  const fee = (item[key] ?? 0) as number;
  if (fee <= 0) return 0;
  return item.channel === "weidian" ? fee * item.quantity : fee;
}

// ─── Checkbox component ───────────────────────────────────────────────────────
function Checkbox({
  checked,
  size = 16,
  onClick,
}: {
  checked: boolean;
  size?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-checked={checked}
      role="checkbox"
      style={{
        width: size,
        height: size,
        border: `1.5px solid ${checked ? "var(--color-primary-fill)" : "var(--color-border)"}`,
        borderRadius: size === 18 ? 5 : 4,
        background: checked ? "var(--color-primary-fill)" : "#fff",
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        padding: 0,
        cursor: "pointer",
      }}
    >
      {checked && (
        <span style={{ color: "#fff", fontSize: size - 6, lineHeight: 1 }}>✓</span>
      )}
    </button>
  );
}

// ─── Cart Skeleton ────────────────────────────────────────────────────────────
function CartSkeleton() {
  return (
    <TgPage tabbar={false}>
      <div className="tg-header" style={{ borderBottom: "1px solid var(--color-border)" }}>
        <div style={{ width: 28 }} />
        <div className="tg-header__t">
          <TgSkeleton height={14} width={120} />
        </div>
        <div style={{ width: 28 }} />
      </div>
      <div className="tg-body" style={{ flex: 1 }}>
        {[1, 2].map((k) => (
          <div
            key={k}
            className="tg-card"
            style={{ padding: 0, overflow: "hidden" }}
          >
            <div style={{ padding: "10px 12px", background: "var(--color-surface)" }}>
              <TgSkeleton height={14} width="60%" />
            </div>
            {[1, 2].map((j) => (
              <div
                key={j}
                style={{
                  padding: "10px 12px",
                  display: "grid",
                  gridTemplateColumns: "14px 44px 1fr auto",
                  gap: 8,
                  alignItems: "center",
                  borderBottom: j < 2 ? "1px solid var(--color-border)" : "none",
                }}
              >
                <TgSkeleton height={14} width={14} />
                <TgSkeleton height={44} width={44} radius={8} />
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <TgSkeleton height={11} width="80%" />
                  <TgSkeleton height={9} width="50%" />
                </div>
                <TgSkeleton height={14} width={56} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </TgPage>
  );
}

// ─── Pending-order prompt (mini variant) ──────────────────────────────────────
function PendingOrderPrompt({
  authToken,
  fallback,
}: {
  authToken: string | null;
  fallback: ReactNode;
}) {
  const { t } = useTranslation("cart");
  const pendingQ = useOrders(authToken, { status: "WAIT_PAYMENT", pageNo: 1, pageSize: 5 });
  const pending = pendingQ.data?.records ?? [];
  if (pendingQ.isLoading || pendingQ.isError || pending.length === 0) {
    return <>{fallback}</>;
  }
  const singleOrder = pending.length === 1 ? pending[0] : null;
  const href = singleOrder
    ? (`/orders` as never)
    : (`/orders` as never);
  return (
    <div
      style={{
        padding: "32px 20px",
        textAlign: "center",
        background: "#fff",
        border: "1px solid var(--color-border)",
        borderRadius: 14,
      }}
    >
      <div style={{ fontSize: 36, marginBottom: 10 }}>🧾</div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 800,
          marginBottom: 6,
        }}
      >
        {t("pending_order.title")}
      </div>
      <p
        style={{
          fontSize: 11,
          color: "var(--color-text-muted)",
          lineHeight: 1.5,
          margin: "0 0 14px",
        }}
      >
        {t("pending_order.body_single")}
      </p>
      <Link
        to={href}
        style={{
          display: "inline-block",
          padding: "8px 18px",
          background: "var(--color-cta)",
          color: "#fff",
          borderRadius: 999,
          fontWeight: 800,
          fontSize: 12,
        }}
      >
        {singleOrder
          ? t("pending_order.cta_multiple")
          : t("pending_order.cta_multiple")}
      </Link>
      <div style={{ marginTop: 10 }}>
        <Link
          to={"/catalog" as never}
          style={{
            color: "var(--color-text-muted)",
            fontSize: 11,
            fontWeight: 600,
            textDecoration: "underline",
          }}
        >
          {t("pending_order.continue_shopping")}
        </Link>
      </div>
    </div>
  );
}

// ─── Empty cart ───────────────────────────────────────────────────────────────
function EmptyCart() {
  const { t } = useTranslation("cart");
  return (
    <TgEmpty
      icon="🛒"
      title={t("state.empty_t")}
      hint={t("state.empty_s")}
      cta={
        <Link
          to={"/catalog" as never}
          style={{
            display: "inline-block",
            padding: "8px 18px",
            background: "var(--color-cta)",
            color: "#fff",
            borderRadius: 999,
            fontWeight: 800,
            fontSize: 12,
          }}
        >
          {t("state.empty_cta")}
        </Link>
      }
    />
  );
}

// ─── Main CartPage ────────────────────────────────────────────────────────────
export function CartPage() {
  const { t } = useTranslation("cart");
  const navigate = useNavigate();
  const { token: authToken, status: authStatus } = useAuth();

  const cartQ = useCart(authToken);
  const editM = useEditCart(authToken);
  const deleteM = useDeleteCart(authToken);

  const addingToCart = useIsMutating({ mutationKey: ADD_CART_MUTATION_KEY }) > 0;

  const rateQ = useExchangeRate(API_BASE_URL);
  const rubPerYuan = rateQ.data?.rubPerYuan ?? null;

  const platforms: CartPlatformGroup[] = cartQ.data?.platforms ?? [];
  const totalItems = cartQ.data?.itemCount ?? 0;
  const [justCleared, setJustCleared] = useState(false);

  // Local selection cache (optimistic toggle)
  const [localSel, setLocalSel] = useState<Record<string, boolean>>({});
  const isSel = useCallback(
    (item: CartItem): boolean => localSel[item.id] ?? item.isSelected,
    [localSel],
  );

  const selectedItems = useMemo(
    () => platforms.flatMap((p) => p.shops.flatMap((s) => s.items.filter((i) => isSel(i)))),
    [platforms, isSel],
  );

  const selectedSubtotal = selectedItems.reduce(
    (acc, i) => acc + (i.totalPriceRubKopecks ?? i.priceRubKopecks * i.quantity),
    0,
  );
  const selectedSubtotalCnyFen =
    rubPerYuan != null ? convertKopecksToFen(selectedSubtotal, rubPerYuan) : undefined;

  const isAllSel =
    totalItems > 0 &&
    platforms.flatMap((p) => p.shops.flatMap((s) => s.items)).every((i) => isSel(i));

  // Toggle helpers
  const toggleItem = (item: CartItem) => {
    const next = !isSel(item);
    setLocalSel((prev) => ({ ...prev, [item.id]: next }));
    editM.mutate({ id: item.id, channel: item.channel, isSelected: next });
  };
  const toggleShop = (shop: CartPlatformGroup["shops"][number]) => {
    const allSel = shop.items.every((i) => isSel(i));
    const next: Record<string, boolean> = {};
    for (const it of shop.items) next[it.id] = !allSel;
    setLocalSel((prev) => ({ ...prev, ...next }));
    for (const it of shop.items)
      editM.mutate({ id: it.id, channel: it.channel, isSelected: !allSel });
  };
  const togglePlatform = (p: CartPlatformGroup) => {
    const all = p.shops.flatMap((s) => s.items);
    const allSel = all.every((i) => isSel(i));
    const next: Record<string, boolean> = {};
    for (const it of all) next[it.id] = !allSel;
    setLocalSel((prev) => ({ ...prev, ...next }));
    for (const it of all) editM.mutate({ id: it.id, channel: it.channel, isSelected: !allSel });
  };
  const toggleAll = () => {
    const all = platforms.flatMap((p) => p.shops.flatMap((s) => s.items));
    const allSel = all.every((i) => isSel(i));
    const next: Record<string, boolean> = {};
    for (const it of all) next[it.id] = !allSel;
    setLocalSel((prev) => ({ ...prev, ...next }));
    for (const it of all) editM.mutate({ id: it.id, channel: it.channel, isSelected: !allSel });
  };

  const removeItem = (item: CartItem) => deleteM.mutate([item.id]);

  // ─── Auth gate ───
  if (authStatus === "booting") return <CartSkeleton />;
  if (authStatus === "signed-out") {
    return (
      <TgPage tabbar={false}>
        <TgHeader title={t("head.title")} back />
        <div className="tg-body" style={{ flex: 1 }}>
          <TgEmpty
            icon="🔑"
            title={t("state.signin_t")}
            hint={t("state.signin_s")}
            cta={
              <Link
                to={"/login" as never}
                style={{
                  display: "inline-block",
                  padding: "8px 18px",
                  background: "var(--color-cta)",
                  color: "#fff",
                  borderRadius: 999,
                  fontWeight: 800,
                  fontSize: 12,
                }}
              >
                {t("state.signin_cta")}
              </Link>
            }
          />
        </div>
      </TgPage>
    );
  }

  const view = resolveCartView({
    cartLoading: cartQ.isLoading,
    hasData: !!cartQ.data,
    cartFetching: cartQ.isFetching,
    addingToCart,
    totalItems,
  });

  if (view === "skeleton") return <CartSkeleton />;

  if (view === "empty") {
    const emptyContent = justCleared ? (
      <EmptyCart />
    ) : (
      <PendingOrderPrompt authToken={authToken} fallback={<EmptyCart />} />
    );
    return (
      <TgPage tabbar={false}>
        <TgHeader title={t("head.title")} back />
        <div className="tg-body" style={{ flex: 1 }}>
          {emptyContent}
        </div>
      </TgPage>
    );
  }

  // ─── cart view ───
  const totalSelected = selectedItems.length;
  const { primary: totalPrimary, secondary: totalSecondary } = formatDual(
    selectedSubtotal,
    "ru-RU",
    selectedSubtotalCnyFen,
  );

  const handleCheckout = () => {
    const ids = selectedItems.map((i) => i.id).join(",");
    navigate({ to: "/confirm-pay" as never, search: { items: ids } as never });
  };

  return (
    <TgPage tabbar={false}>
      <TgHeader
        title={`${t("head.title")} · ${totalItems}`}
        back
        right={
          <button
            type="button"
            style={{
              background: "transparent",
              border: 0,
              color: "var(--color-text-muted)",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              padding: "4px 0",
            }}
            onClick={() => {
              const all = platforms.flatMap((p) =>
                p.shops.flatMap((s) => s.items.map((i) => i.id)),
              );
              if (all.length > 0) {
                setJustCleared(true);
                deleteM.mutate(all);
              }
            }}
          >
            {t("head.clear_all")}
          </button>
        }
      />

      <div className="tg-body" style={{ flex: 1 }}>
        {/* 合单优势说明 */}
        <div
          style={{
            background: "var(--color-primary-soft)",
            border: "1px solid rgba(11,168,136,.25)",
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 11,
            lineHeight: 1.5,
            color: "var(--color-text)",
          }}
        >
          <strong style={{ color: "var(--color-primary-fill)" }}>
            {t("explainer.title")}:
          </strong>{" "}
          {t("group.platform_items", { count: totalItems })} ·{" "}
          {t("summary.subtitle")}
        </div>

        {/* Platform groups */}
        {platforms.map((pg) => {
          const plat = PLATFORM_LABEL[pg.channel] ?? { icon: "🛒", label: pg.channel };
          const platAllSel = pg.shops.flatMap((s) => s.items).every((i) => isSel(i));
          return (
            <div
              key={pg.channel}
              className="tg-card"
              style={{ padding: 0, overflow: "hidden" }}
            >
              {/* Platform header */}
              <div
                style={{
                  padding: "10px 12px",
                  background:
                    "linear-gradient(180deg,var(--color-text),#1A2924)",
                  color: "#fff",
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <Checkbox
                  checked={platAllSel}
                  size={18}
                  onClick={() => togglePlatform(pg)}
                />
                <span
                  style={{
                    padding: "2px 8px",
                    background: "rgba(255,255,255,.12)",
                    borderRadius: 999,
                    fontSize: 9,
                    fontWeight: 800,
                  }}
                >
                  {plat.icon} {plat.label}
                </span>
                <strong style={{ fontSize: 12, color: "#fff" }}>
                  {t("group.platform_items", { count: pg.itemCount })} · {pg.shops.length}
                </strong>
              </div>

              {/* Shops */}
              {pg.shops.map((shop, shopIdx) => {
                const shopAllSel = shop.items.every((i) => isSel(i));
                const shopInitial =
                  (shop.shopName ?? plat.label).slice(0, 1).toUpperCase();
                return (
                  <div key={shop.shopCode ?? shopIdx}>
                    {/* Shop sub-header */}
                    <div
                      style={{
                        padding: "8px 12px",
                        background: "var(--color-surface)",
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        borderBottom: "1px solid var(--color-border)",
                      }}
                    >
                      <Checkbox
                        checked={shopAllSel}
                        size={16}
                        onClick={() => toggleShop(shop)}
                      />
                      {shop.shopLogoUrl ? (
                        <img
                          src={shop.shopLogoUrl}
                          alt={shop.shopName ?? ""}
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 6,
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <span
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 6,
                            background:
                              "linear-gradient(135deg,var(--color-primary-soft),var(--color-primary))",
                            display: "grid",
                            placeItems: "center",
                            fontFamily: "var(--font-display)",
                            fontWeight: 900,
                            fontSize: 9,
                            color: "var(--color-primary-fill)",
                          }}
                        >
                          {shopInitial}
                        </span>
                      )}
                      <strong style={{ fontSize: 11, flex: 1, minWidth: 0 }}>
                        {shop.shopName ?? plat.label}
                      </strong>
                    </div>

                    {/* Items */}
                    {shop.items.map((item, itemIdx) => {
                      const selected = isSel(item);
                      const { primary: itemPrimary } = formatDual(
                        item.priceRubKopecks * item.quantity,
                        "ru-RU",
                        item.originalCnyFen != null
                          ? item.originalCnyFen * item.quantity
                          : undefined,
                      );
                      const cnyDisplay =
                        item.originalCnyFen != null
                          ? formatPrice(item.originalCnyFen, "zh-CN", "CNY")
                          : null;
                      const skuDisplay =
                        item.skuAttrEnumFormatList &&
                        item.skuAttrEnumFormatList.length > 0
                          ? item.skuAttrEnumFormatList
                              .map((a) => `${a.value}`)
                              .join(" · ")
                          : item.skuRemarks;
                      return (
                        <div
                          key={item.id}
                          style={{
                            padding: "10px 12px",
                            display: "grid",
                            gridTemplateColumns: "14px 44px 1fr auto",
                            gap: 8,
                            alignItems: "center",
                            borderBottom:
                              itemIdx < shop.items.length - 1
                                ? "1px solid var(--color-border)"
                                : "none",
                            opacity: item.isExpired ? 0.5 : 1,
                          }}
                        >
                          <Checkbox
                            checked={selected}
                            size={14}
                            onClick={() => toggleItem(item)}
                          />
                          {/* Thumbnail */}
                          <div
                            style={{
                              width: 44,
                              height: 44,
                              borderRadius: 8,
                              background: "var(--color-surface)",
                              display: "grid",
                              placeItems: "center",
                              fontSize: 20,
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            {item.imageUrl ? (
                              <img
                                src={item.imageUrl}
                                alt={item.title}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              "🛍️"
                            )}
                          </div>
                          {/* Info */}
                          <div>
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                lineHeight: 1.3,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {item.title}
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                color: "var(--color-text-muted)",
                                marginTop: 2,
                              }}
                            >
                              {cnyDisplay ? `${cnyDisplay} · ` : ""}
                              {skuDisplay ? `${skuDisplay} · ` : ""}× {item.quantity}
                            </div>
                            {/* Remove button */}
                            <button
                              type="button"
                              onClick={() => removeItem(item)}
                              style={{
                                marginTop: 4,
                                background: "transparent",
                                border: 0,
                                padding: 0,
                                fontSize: 9,
                                color: "var(--color-text-muted)",
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                            >
                              {t("row.delete")}
                            </button>
                          </div>
                          {/* Price */}
                          <div
                            style={{
                              fontFamily: "var(--font-display)",
                              fontSize: 12,
                              fontWeight: 900,
                              color: "var(--color-price)",
                              textAlign: "right",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {itemPrimary}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* ── Sticky bottom checkout bar (design: position:sticky bottom:0) ── */}
      <div
        style={{
          position: "sticky",
          bottom: 0,
          background: "#fff",
          borderTop: "1px solid var(--color-border)",
          padding: "10px 12px",
          display: "flex",
          gap: 10,
          alignItems: "center",
          zIndex: 10,
        }}
      >
        {/* 全选 */}
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <Checkbox checked={isAllSel} size={16} onClick={toggleAll} />
          {t("bulk.select_all", { selected: totalSelected, total: totalItems })}
        </label>

        {/* Totals */}
        <div style={{ flex: 1, textAlign: "right" }}>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>
            {t("summary.items_count", { count: totalSelected })}
          </div>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 18,
              fontWeight: 900,
              color: "var(--color-price)",
            }}
          >
            {totalPrimary}
          </div>
          {totalSecondary && (
            <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>
              {totalSecondary}
            </div>
          )}
        </div>

        {/* 去结算 */}
        <button
          type="button"
          disabled={totalSelected === 0}
          onClick={handleCheckout}
          style={{
            background: totalSelected > 0 ? "var(--color-cta)" : "var(--color-border)",
            color: "#fff",
            padding: "12px 22px",
            borderRadius: 999,
            fontWeight: 800,
            fontSize: 13,
            border: 0,
            cursor: totalSelected > 0 ? "pointer" : "not-allowed",
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          {t("summary.checkout", { count: totalSelected })}
        </button>
      </div>
    </TgPage>
  );
}
